import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { type ComponentProps, splitProps } from "solid-js";
import { cn } from "@/libs/cn";

export const badgeVariants = cva(
  "inline-flex items-center rounded-md border text-xs font-semibold transition-shadow focus-visible:outline-hidden focus-visible:ring-[1.5px] focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Decorative, never interactive: no `hover:` at all. `--secondary` and `--accent` are
        // the same colour, so a `secondary` chip disappears the moment the pointer is over it
        // inside a `hover:bg-accent` container — and `tailwind-merge` cannot fix that from the
        // outside, because a base `bg-white` does not cancel a `hover:bg-secondary/80`.
        skill: "bg-white border-gray-300 text-gray-900",
        // Decorative twins of `light-primary` and `info`, for the same reason as `skill` above:
        // a chip that sits inside a container carrying its own `hover:` (a recent-search row is
        // one large `hover:bg-accent` button) must not restyle itself when the pointer is over
        // the *container*. It has to be born without a `hover:`, because a base `bg-*` added
        // from the outside does not cancel one — see `SkillBadge`'s call sites.
        // `secondary` has no such twin on purpose: `--secondary` and `--accent` are the same
        // colour, so that chip is invisible over a hovered row whatever its hover state is.
        "light-primary-static": "bg-primary/10 text-primary",
        "info-static": "bg-blue-100 text-blue-800",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80",
        outline: "text-foreground",
        "light-primary": "bg-primary/10 text-primary hover:bg-primary/20",
        "light-green": "bg-green-500/10 text-green-500 hover:bg-green-500/20",
        "light-indigo":
          "bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20",
        warning:
          "bg-notification text-notification-foreground hover:bg-notification/80",
        success: "bg-green-600 text-white",
        info: "bg-blue-100 text-blue-800 hover:bg-blue-200",
        disabled: "bg-gray-200 text-gray-500",
      },
      size: {
        default: "text-xs px-2 py-0.5",
        sm: "text-xs px-2 py-0.5",
        md: "text-sm px-3 py-1",
        lg: "text-lg px-4 py-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type BadgeProps = ComponentProps<"div"> &
  VariantProps<typeof badgeVariants>;

export const Badge = (props: BadgeProps) => {
  const [local, rest] = splitProps(props, [
    "class",
    "variant",
    "size",
    "classList",
  ]);

  return (
    <div
      class={cn(
        badgeVariants({
          variant: local.variant,
          size: local.size,
        }),
        local.class,
      )}
      classList={local.classList}
      {...rest}
    />
  );
};
